import { useState, useMemo, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import * as Sentry from '@sentry/react';
import { useCostTracking } from '../../contexts/CostTrackingContext';
import { useEquipmentContext } from '../../contexts/EquipmentContext';
import { filterExpenses, filterExpensesByCategory } from '../../utils/costTrackingFilterUtils';
import { expenseInvoiceLabel, expenseWorkDescriptionLabel, formatExpenseDate, laborCostOfRecord } from '../../utils/costTrackingUtils';
import { PM_INTERNAL_SERVICE_CATEGORY } from '../../utils/pmCostTracking';
import { deleteExpenseRecord } from '../../graphql/costTrackingQueries';
import type { ExpenseFilterCriteria, ExpenseCategoryEntry, ExpenseRecord } from '../../types/costTrackingTypes';
import { Btn, DataTable, IconActions, Pagination, type DataTableColumn, type SortDirection } from '../ui';
import { DeleteExpenseRecordModal } from './DeleteExpenseRecordModal';
import '../../css/CostTrackingDashboard.css';

const PAGE_SIZE = 25;
const MENU_GAP = 4;
const MENU_VIEWPORT_PAD = 8;

function positionActionMenu(btn: HTMLElement, menu: HTMLElement) {
    const rect = btn.getBoundingClientRect();
    const menuHeight = menu.offsetHeight;
    const menuWidth = menu.offsetWidth || 140;
    const spaceBelow = window.innerHeight - rect.bottom - MENU_VIEWPORT_PAD;
    const openUp = spaceBelow < menuHeight && rect.top - MENU_VIEWPORT_PAD > spaceBelow;

    let top = openUp ? rect.top - menuHeight - MENU_GAP : rect.bottom + MENU_GAP;
    top = Math.min(
        Math.max(top, MENU_VIEWPORT_PAD),
        Math.max(MENU_VIEWPORT_PAD, window.innerHeight - menuHeight - MENU_VIEWPORT_PAD),
    );

    let left = rect.right - menuWidth;
    left = Math.min(
        Math.max(left, MENU_VIEWPORT_PAD),
        Math.max(MENU_VIEWPORT_PAD, window.innerWidth - menuWidth - MENU_VIEWPORT_PAD),
    );

    return { top, left };
}

interface ExpenseTableProps {
    activeCategory: string | null;
    filterCriteria: ExpenseFilterCriteria;
    searchQuery: string;
    onViewExpense: (expenseId: string) => void;
    onEditExpense: (expenseId: string) => void;
}

/**
 * Expense table with columns: Date of Service, Item, Vendor, Description, Category, Total.
 * Supports pagination, text search filtering, and a three-dot action menu per row.
 */
export const ExpenseTable = ({
    activeCategory,
    filterCriteria,
    searchQuery,
    onViewExpense,
    onEditExpense,
}: ExpenseTableProps) => {
    const { expenseRecords, refreshExpenseData } = useCostTracking();
    const { companyGroupedEquipmentData } = useEquipmentContext();

    const unitNameMap = useMemo(() => {
        const map = new Map<string, string>();
        for (const group of (companyGroupedEquipmentData || [])) {
            for (const unit of (group.units || [])) {
                map.set(unit.id, unit.name);
            }
        }
        return map;
    }, [companyGroupedEquipmentData]);

    const getUnitDisplayName = useCallback(
        (unitId: string) => unitNameMap.get(unitId) || unitId,
        [unitNameMap],
    );

    const [currentPage, setCurrentPage] = useState(1);
    const [sortColumnId, setSortColumnId] = useState('date');
    const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
    const [deletingRecord, setDeletingRecord] = useState<ExpenseRecord | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [openMenuId, setOpenMenuId] = useState<string | null>(null);
    const [menuPos, setMenuPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
    const menuRef = useRef<HTMLDivElement>(null);
    const btnRefs = useRef<Map<string, HTMLElement>>(new Map());

    // Reset to page 1 when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [activeCategory, filterCriteria, searchQuery]);

    const openMenu = useCallback((recordId: string) => {
        setOpenMenuId((current) => (current === recordId ? null : recordId));
    }, []);

    useLayoutEffect(() => {
        if (!openMenuId) return;
        const btn = btnRefs.current.get(openMenuId);
        const menu = menuRef.current;
        if (!btn || !menu) return;

        const updatePosition = () => setMenuPos(positionActionMenu(btn, menu));
        updatePosition();

        window.addEventListener('resize', updatePosition);
        window.addEventListener('scroll', updatePosition, true);
        return () => {
            window.removeEventListener('resize', updatePosition);
            window.removeEventListener('scroll', updatePosition, true);
        };
    }, [openMenuId]);

    // Close menu on outside click
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
                // Also check if the click was on a menu button
                let clickedBtn = false;
                btnRefs.current.forEach((btn) => {
                    if (btn.contains(e.target as Node)) clickedBtn = true;
                });
                if (!clickedBtn) setOpenMenuId(null);
            }
        };
        if (openMenuId) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [openMenuId]);

    const formatDate = formatExpenseDate;

    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
    };

    const getCategoryNames = (record: ExpenseRecord): string => {
        const names: string[] = [];
        if (record.expenseCategories) {
            try {
                const cats: ExpenseCategoryEntry[] = JSON.parse(record.expenseCategories);
                for (const cat of cats) {
                    if (cat.categoryName && !names.includes(cat.categoryName)) {
                        names.push(cat.categoryName);
                    }
                }
            } catch {
                // Leave the parsed names already collected.
            }
        }
        if (
            (record.pmServiceLevelName || (record.pmExpense ?? 0) > 0)
            && !names.includes(PM_INTERNAL_SERVICE_CATEGORY)
        ) {
            names.unshift('PM Service');
        }
        return names.join(', ') || '—';
    };

    // Apply category filter, then advanced filters, then search.
    const filteredRecords = useMemo(() => {
        let records = expenseRecords;

        // Category tab filter
        if (activeCategory) {
            records = filterExpensesByCategory(records, activeCategory);
        }

        // Advanced filters
        const hasCriteria = filterCriteria.dateRange || filterCriteria.equipmentId
            || filterCriteria.vendorId || filterCriteria.categoryName;
        if (hasCriteria) {
            records = filterExpenses(records, filterCriteria);
        }

        // Text search
        if (searchQuery.trim()) {
            records = filterExpenses(records, { searchQuery: searchQuery.trim() });
        }

        const sortColumn = sortColumnId;
        const direction = sortDirection === 'asc' ? 1 : -1;
        return [...records].sort((a, b) => {
            const valueFor = (record: ExpenseRecord) => {
                switch (sortColumn) {
                    case 'item':
                        return getUnitDisplayName(record.unitId);
                    case 'vendor':
                        return record.vendorName || '';
                    case 'description':
                        return expenseWorkDescriptionLabel(record.workDescription);
                    case 'category':
                        return getCategoryNames(record);
                    case 'total':
                        return record.totalExpense;
                    default:
                        return new Date(record.dateOfService).getTime();
                }
            };
            const aValue = valueFor(a);
            const bValue = valueFor(b);
            if (typeof aValue === 'number' && typeof bValue === 'number') {
                return (aValue - bValue) * direction;
            }
            return String(aValue).localeCompare(String(bValue), undefined, { numeric: true, sensitivity: 'base' }) * direction;
        });
    }, [expenseRecords, activeCategory, filterCriteria, searchQuery, sortColumnId, sortDirection, getUnitDisplayName]);

    // Pagination
    const totalPages = Math.max(1, Math.ceil(filteredRecords.length / PAGE_SIZE));
    const safePage = Math.min(currentPage, totalPages);
    const startIdx = (safePage - 1) * PAGE_SIZE;
    const pageRows = filteredRecords.slice(startIdx, startIdx + PAGE_SIZE);

    const handleConfirmDelete = async () => {
        if (!deletingRecord) return;
        setIsDeleting(true);
        try {
            await deleteExpenseRecord(deletingRecord.id);
            await refreshExpenseData();
            setDeletingRecord(null);
        } catch (err) {
            console.error('Failed to delete expense record:', err);
            Sentry.captureException(err);
        } finally {
            setIsDeleting(false);
        }
    };

    const handleEmail = () => {
        const record = expenseRecords.find((item) => item.id === openMenuId) ?? null;
        setOpenMenuId(null);
        if (!record) return;
        const subject = `Cost entry ${formatDate(record.dateOfService)}`;
        const lines = [
            getUnitDisplayName(record.unitId),
            formatDate(record.dateOfService),
            record.vendorName,
            expenseWorkDescriptionLabel(record.workDescription),
            expenseInvoiceLabel(record.invoiceNumber) ? `Invoice ${expenseInvoiceLabel(record.invoiceNumber)}` : '',
            `Labor ${formatCurrency(laborCostOfRecord(record))}`,
            `Total ${formatCurrency(record.totalExpense)}`,
        ].filter(Boolean);
        window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(lines.join('\n'))}`;
    };

    const deleteLabel = deletingRecord
        ? `${getUnitDisplayName(deletingRecord.unitId)} · ${formatDate(deletingRecord.dateOfService)}`
        : null;

    const columns = useMemo<DataTableColumn<ExpenseRecord>[]>(() => [
        {
            id: 'date',
            header: 'Date of Service',
            sortable: true,
            sortType: 'numeric',
            sortValue: (record) => new Date(record.dateOfService).getTime(),
            renderCell: (record) => formatDate(record.dateOfService),
        },
        {
            id: 'item',
            header: 'Item',
            sortable: true,
            sortType: 'alpha',
            sortValue: (record) => getUnitDisplayName(record.unitId),
            mobileCardPrimary: true,
            renderCell: (record) => getUnitDisplayName(record.unitId),
        },
        {
            id: 'vendor',
            header: 'Vendor',
            sortable: true,
            sortType: 'alpha',
            sortValue: (record) => record.vendorName || '',
            renderCell: (record) => record.vendorName || '—',
        },
        {
            id: 'description',
            header: 'Description',
            sortable: true,
            sortType: 'alpha',
            sortValue: (record) => expenseWorkDescriptionLabel(record.workDescription),
            renderCell: (record) => expenseWorkDescriptionLabel(record.workDescription) || '—',
        },
        {
            id: 'category',
            header: 'Category',
            sortable: true,
            sortType: 'alpha',
            sortValue: (record) => getCategoryNames(record),
            renderCell: (record) => getCategoryNames(record),
        },
        {
            id: 'total',
            header: 'Total',
            align: 'right',
            sortable: true,
            sortType: 'numeric',
            sortValue: (record) => record.totalExpense,
            renderCell: (record) => formatCurrency(record.totalExpense),
        },
        {
            id: 'actions',
            header: 'Actions',
            headerLabel: 'Actions',
            align: 'center',
            thClassName: 'data-table-col-narrow',
            tdClassName: 'data-table-col-narrow',
            mobileCardAction: true,
            mobileHideLabel: true,
            renderCell: (record) => (
                <span
                    className="ct-action-menu-anchor"
                    ref={(el) => { if (el) btnRefs.current.set(record.id, el); }}
                >
                    <Btn
                        buttonClasses="btn-action-menu"
                        buttonIcon={<IconActions />}
                        ariaLabel="Actions menu"
                        buttonAriaProps={{
                            'aria-haspopup': 'menu',
                            'aria-expanded': openMenuId === record.id,
                        }}
                        buttonOnClick={() => openMenu(record.id)}
                    />
                </span>
            ),
        },
    ], [getUnitDisplayName, openMenu, openMenuId]);

    return (
        <div className="ct-table-panel">
            <DataTable
                ariaLabel="Cost tracking expenses"
                columns={columns}
                rows={pageRows}
                getRowKey={(record) => record.id}
                mobileCardLayout
                sortColumnId={sortColumnId}
                sortDirection={sortDirection}
                onSortChange={(columnId, direction) => {
                    setSortColumnId(columnId);
                    setSortDirection(direction);
                }}
                emptyState={(
                    <div className="ct-table-empty">
                        {searchQuery.trim()
                            ? 'No expenses match your search'
                            : 'No expense records found'}
                    </div>
                )}
            />

            {filteredRecords.length > 0 && totalPages > 1 && (
                <div className="ct-pagination">
                    <Pagination
                        currentPage={safePage}
                        totalPages={totalPages}
                        onPageChange={setCurrentPage}
                        ariaLabel="Cost tracking expenses pagination"
                    />
                </div>
            )}

            {openMenuId && createPortal(
                <div
                    ref={menuRef}
                    className="ct-action-dropdown"
                    role="menu"
                    style={{
                        position: 'fixed',
                        top: menuPos.top,
                        left: menuPos.left,
                    }}
                >
                    <button type="button" role="menuitem" onClick={() => { onViewExpense(openMenuId); setOpenMenuId(null); }}>View</button>
                    <button type="button" role="menuitem" onClick={() => { onEditExpense(openMenuId); setOpenMenuId(null); }}>Edit</button>
                    <button
                        type="button"
                        role="menuitem"
                        onClick={() => {
                            const record = expenseRecords.find((item) => item.id === openMenuId) ?? null;
                            setOpenMenuId(null);
                            setDeletingRecord(record);
                        }}
                    >
                        Delete
                    </button>
                    <button type="button" role="menuitem" onClick={handleEmail}>Email</button>
                </div>,
                document.body,
            )}

            <DeleteExpenseRecordModal
                recordLabel={deleteLabel}
                isDeleting={isDeleting}
                onDismiss={() => { if (!isDeleting) setDeletingRecord(null); }}
                onConfirmDelete={handleConfirmDelete}
            />
        </div>
    );
};
